import { useMemo, useState } from 'react';
import type { CustomerSensor } from '@metron/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Search } from 'lucide-react';

export interface SensorPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensors: CustomerSensor[];
  existingSensorIds: Set<number>;
  onAddSubmeters: (ids: number[]) => void;
  onAddMasters: (ids: number[]) => void;
}

export function SensorPicker({
  open,
  onOpenChange,
  sensors,
  existingSensorIds,
  onAddSubmeters,
  onAddMasters,
}: SensorPickerProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const filteredSensors = useMemo(() => {
    if (!search.trim()) return sensors;
    const query = search.toLowerCase();
    return sensors.filter(
      (s) =>
        s.deviceId.toLowerCase().includes(query) ||
        s.address?.toLowerCase().includes(query),
    );
  }, [sensors, search]);

  const handleToggle = (sensorId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sensorId)) {
        next.delete(sensorId);
      } else {
        next.add(sensorId);
      }
      return next;
    });
  };

  const handleAddSubmeters = () => {
    onAddSubmeters(Array.from(selectedIds));
    setSelectedIds(new Set());
    setSearch('');
    onOpenChange(false);
  };

  const handleAddMasters = () => {
    onAddMasters(Array.from(selectedIds));
    setSelectedIds(new Set());
    setSearch('');
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedIds(new Set());
      setSearch('');
    }
    onOpenChange(nextOpen);
  };

  const selectedCount = selectedIds.size;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-2xl max-h-[80vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>Add Sensors</DialogTitle>
          <DialogDescription>
            Search and select sensors to add to this custom group.
          </DialogDescription>
        </DialogHeader>

        <div className='relative'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search by device ID or address...'
            className='pl-9'
          />
        </div>

        <div className='flex-1 overflow-y-auto border rounded-md min-h-[200px] max-h-[400px]'>
          {filteredSensors.length === 0
            ? (
              <div className='flex items-center justify-center py-8'>
                <p className='text-sm text-muted-foreground'>
                  {search ? 'No sensors match your search' : 'No sensors available'}
                </p>
              </div>
            )
            : (
              <div className='divide-y'>
                {filteredSensors.map((sensor) => {
                  const isExisting = existingSensorIds.has(sensor.sensorId);
                  const isSelected = selectedIds.has(sensor.sensorId);

                  return (
                    <label
                      key={sensor.sensorId}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 ${
                        isExisting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggle(sensor.sensorId)}
                        disabled={isExisting}
                      />
                      <div className='flex-1 min-w-0'>
                        <span className='text-sm font-mono'>{sensor.deviceId}</span>
                        {sensor.address && (
                          <span className='text-xs text-muted-foreground ml-2'>
                            {sensor.address}
                          </span>
                        )}
                      </div>
                      {isExisting && (
                        <span className='text-xs text-muted-foreground shrink-0'>
                          Already assigned
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
        </div>

        <DialogFooter className='flex-row gap-2 sm:justify-between'>
          <span className='text-sm text-muted-foreground self-center'>
            {selectedCount > 0 ? `${selectedCount} selected` : 'None selected'}
          </span>
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={handleAddSubmeters}
              disabled={selectedCount === 0}
            >
              Add as Submeters
            </Button>
            <Button
              onClick={handleAddMasters}
              disabled={selectedCount === 0}
            >
              Add as Masters
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
